import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useContent } from "../content-context";
import { Eyebrow, Reveal } from "./Reveal";

export default function Island() {
  const { island } = useContent();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section id="island" ref={ref} data-testid="island-section" className="relative overflow-hidden bg-ivory py-24 md:py-32">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-16 px-5 md:px-10 lg:grid-cols-12 lg:gap-8">
        <div className="flex flex-col justify-center lg:col-span-5 lg:pr-10">
          <Reveal>
            <Eyebrow>{island.eyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-4xl font-medium leading-[1.05] text-navy md:text-6xl" data-testid="island-heading">
              An entire island, <em className="text-gold">kept just for you</em>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-md text-sm font-light leading-relaxed text-navy/75 md:text-base" data-testid="island-body-1">
              {island.body1}
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-5 max-w-md text-sm font-light leading-relaxed text-navy/75 md:text-base" data-testid="island-body-2">
              {island.body2}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-12 grid grid-cols-3 gap-6 border-t border-navy/10 pt-8" data-testid="island-stats">
              {island.stats.map((s, i) => (
                <div key={i}>
                  <p className="font-display text-4xl text-gold md:text-5xl">{s.value}</p>
                  <p className="mt-2 text-[10px] uppercase tracking-[0.2em] text-navy/60 md:text-xs">{s.label}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="relative lg:col-span-7">
          <Reveal className="relative">
            <div className="relative aspect-[4/3] overflow-hidden lg:aspect-[16/11]">
              <motion.img
                src={island.mainImage}
                alt={island.mainAlt}
                style={{ y: imgY }}
                className="absolute inset-0 h-[112%] w-full object-cover"
                data-testid="island-main-image"
              />
            </div>
            <div className="absolute -bottom-10 -left-2 hidden w-56 overflow-hidden border-4 border-ivory shadow-[0_8px_32px_rgba(10,25,47,0.18)] sm:block md:w-72 lg:-left-14">
              <img src={island.overlapImage} alt={island.overlapAlt} className="aspect-[4/3] w-full object-cover" data-testid="island-overlap-image" />
            </div>
            <p className="mt-6 hidden text-right text-[10px] uppercase tracking-widest2 text-navy/40 lg:block">
              The Atlantic, as seen from the island
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
